import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useToast } from '../context/ToastContext';
import Loading from '../components/Loading';

const AdminExpenses = () => {
  const [expenses, setExpenses] = useState([]);
  const [filteredExpenses, setFilteredExpenses] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [employeeFilter, setEmployeeFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [adminComment, setAdminComment] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [paymentType, setPaymentType] = useState('');
  
  const [searchParams] = useSearchParams();
  const toast = useToast();

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const id = searchParams.get('id');
    if (id && expenses.length > 0) {
      const expense = expenses.find(e => e._id === id);
      if (expense) openModal(expense);
    }
  }, [searchParams, expenses]);

  useEffect(() => {
    filterExpenses();
  }, [expenses, statusFilter, employeeFilter, searchTerm]);

  const fetchData = async () => {
    try {
      const [expensesRes, employeesRes] = await Promise.all([
        axios.get('/api/admin/expenses'),
        axios.get('/api/admin/employees'),
      ]);
      setExpenses(expensesRes.data);
      setEmployees(employeesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterExpenses = () => {
    let filtered = expenses;
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(e => e.status === statusFilter);
    }
    
    if (employeeFilter !== 'all') {
      filtered = filtered.filter(e => e.employee?._id === employeeFilter);
    }
    
    if (searchTerm) {
      filtered = filtered.filter(e => 
        e.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        e.employee?.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    setFilteredExpenses(filtered);
  };

  const openModal = (expense) => {
    setSelectedExpense(expense);
    setNewStatus(expense.status);
    setAdminComment(expense.adminComment || '');
    setPaymentType(expense.paymentType || 'reimbursement');
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedExpense(null);
    setAdminComment('');
    setNewStatus('');
  };

  const handleUpdate = async () => {
    if (!newStatus) {
      return toast.error('Please select a status');
    }

    setUpdating(true);
    try {
      const res = await axios.patch(`/api/admin/expenses/${selectedExpense._id}/status`, {
        status: newStatus,
        adminComment,
        paymentType,
      });

      setExpenses(prev => prev.map(e => 
        e._id === selectedExpense._id ? res.data : e
      ));
      
      toast.success('Expense updated successfully');
      closeModal();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update expense');
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this expense?')) return;

    try {
      await axios.delete(`/api/admin/expenses/${id}`);
      setExpenses(prev => prev.filter(e => e._id !== id));
      toast.success('Expense deleted successfully');
      closeModal();
    } catch (error) {
      toast.error('Failed to delete expense');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getFileUrl = (path) => {
    const filename = path.split('/').pop();
    return `/uploads/${filename}`;
  };

  if (loading) return <Loading />;

  return (
    <div className="main-content">
      <div className="page-header">
        <div>
          <h1 className="page-title">All Expenses</h1>
          <p className="page-subtitle">Review and manage employee expense reports</p>
        </div>
      </div>

      <div className="filters">
        <input
          type="text"
          className="form-input"
          placeholder="Search by title or employee..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <select
          className="form-select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">All Status</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="paid">Paid</option>
        </select>
        <select
          className="form-select"
          value={employeeFilter}
          onChange={(e) => setEmployeeFilter(e.target.value)}
        >
          <option value="all">All Employees</option>
          {employees.map(emp => (
            <option key={emp._id} value={emp._id}>{emp.name}</option>
          ))}
        </select>
      </div>

      <div className="card">
        <div className="card-body">
          {filteredExpenses.length === 0 ? (
            <div className="empty-state">
              <h3 className="empty-state-title">No expenses found</h3>
              <p className="empty-state-text">Try adjusting your filters</p>
            </div>
          ) : (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Title</th>
                    <th>Category</th>
                    <th>Date</th>
                    <th>Amount</th>
                    <th>Payment</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredExpenses.map(expense => (
                    <tr key={expense._id}>
                      <td>
                        <div style={{ fontWeight: 600 }}>{expense.employee?.name}</div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>
                          {expense.employee?.department}
                        </div>
                      </td>
                      <td>
                        <div style={{ fontWeight: 600 }}>{expense.title}</div>
                        {expense.attachments?.length > 0 && (
                          <div style={{ fontSize: '0.75rem', color: 'var(--primary)' }}>
                            📎 {expense.attachments.length} attachment(s)
                          </div>
                        )}
                      </td>
                      <td>{expense.category}</td>
                      <td>{formatDate(expense.expenseDate)}</td>
                      <td style={{ fontWeight: 600 }}>{formatCurrency(expense.amount)}</td>
                      <td style={{ textTransform: 'capitalize' }}>{expense.paymentType}</td>
                      <td>
                        <span className={`badge badge-${expense.status}`}>
                          {expense.status}
                        </span>
                      </td>
                      <td>
                        <button
                          onClick={() => openModal(expense)}
                          className="btn btn-primary btn-sm"
                        >
                          Review
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Review Modal */}
      {modalOpen && selectedExpense && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px' }}>
            <div className="modal-header">
              <h2 className="modal-title">Review Expense</h2>
              <button onClick={closeModal} className="modal-close">✕</button>
            </div>
            <div className="modal-body">
              <div className="detail-grid" style={{ marginBottom: '24px' }}>
                <div className="detail-item">
                  <span className="detail-label">Employee</span>
                  <span className="detail-value">{selectedExpense.employee?.name}</span>
                  <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>
                    {selectedExpense.employee?.email}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Amount</span>
                  <span className="detail-value" style={{ color: 'var(--primary)', fontWeight: 700 }}>
                    {formatCurrency(selectedExpense.amount)}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Category</span>
                  <span className="detail-value">{selectedExpense.category}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Expense Date</span>
                  <span className="detail-value">{formatDate(selectedExpense.expenseDate)}</span>
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <span className="detail-label">Title</span>
                <p style={{ marginTop: '4px', fontWeight: 600 }}>{selectedExpense.title}</p>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <span className="detail-label">Description</span>
                <p style={{ marginTop: '4px', color: 'var(--gray-700)' }}>{selectedExpense.description}</p>
              </div>

              {selectedExpense.attachments?.length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  <span className="detail-label">Attachments</span>
                  <div className="attachments-grid" style={{ marginTop: '8px' }}>
                    {selectedExpense.attachments.map((file, index) => (
                      <a
                        key={index}
                        href={getFileUrl(file.path)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="attachment-card"
                        style={{ textDecoration: 'none', color: 'inherit' }}
                      >
                        <div className="attachment-preview">
                          {file.mimetype === 'application/pdf' ? (
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                              <polyline points="14 2 14 8 20 8" />
                            </svg>
                          ) : (
                            <img src={getFileUrl(file.path)} alt={file.originalName} />
                          )}
                        </div>
                        <div className="attachment-info">
                          <div className="attachment-name">{file.originalName}</div>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              <hr style={{ border: 'none', borderTop: '1px solid var(--gray-200)', margin: '24px 0' }} />

              <div className="form-group">
                <label className="form-label">Status *</label>
                <select
                  className="form-select"
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                >
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                  <option value="paid">Paid</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Payment Type</label>
                <select
                  className="form-select"
                  value={paymentType}
                  onChange={(e) => setPaymentType(e.target.value)}
                >
                  <option value="reimbursement">Reimbursement</option>
                  <option value="advance">Advance</option>
                  <option value="instant">Instant Payment</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Admin Comment</label>
                <textarea
                  className="form-textarea"
                  placeholder="Add a comment (optional)..."
                  value={adminComment}
                  onChange={(e) => setAdminComment(e.target.value)}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                onClick={() => handleDelete(selectedExpense._id)}
                className="btn btn-danger btn-sm"
                style={{ marginRight: 'auto' }}
              >
                Delete
              </button>
              <button onClick={closeModal} className="btn btn-secondary">Cancel</button>
              <button onClick={handleUpdate} className="btn btn-primary" disabled={updating}>
                {updating ? 'Updating...' : 'Update Expense'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminExpenses;
